import { queryOptions } from '@tanstack/react-query';

import { createSamplingAssignmentApi } from '../apis/gp-sampling-assignment.api';
import { MapActionType } from '../types/gp-sampling-assignment';
import {
  AssignmentMapConfig,
  GP_ASSIGNMENT_CONFIG,
  ngpAssignmentConfig,
  ngpTeamAssignmentConfig,
} from '../../lib/assignment-context';

import { Globals, Filters, getQueriesWithGlobal, useQueriesWithGlobal } from '@data-access';
import { organisationApi } from '@organisations/data-access';

const ASSIGNMENT_MAP_ORGANISATIONS_FILTERS: Filters = {
  page: 1,
  sorting: [],
  filters: [{ id: 'page_size', value: 1000 }],
};

export const samplingAssignmentQueries = (global: Globals, config: AssignmentMapConfig) => {
  const api = createSamplingAssignmentApi({
    clause: config.clause,
    campaignYear: config.campaignYear,
  });

  return {
    all: () => [global, config.queryKey],

    map: (referenceDate?: string) =>
      queryOptions({
        queryKey: [...samplingAssignmentQueries(global, config).all(), 'map', referenceDate ?? 'today'],
        queryFn: () => api.map.get(referenceDate),
      }),

    laboratories: () =>
      queryOptions({
        queryKey: [...samplingAssignmentQueries(global, config).all(), 'laboratories'],
        queryFn: () => api.laboratories.list(),
        enabled:
          config.capabilities.filterLaboratories
          || config.capabilities.drawModes.includes('assign_laboratory'),
      }),

    samplers: (organizationId?: number) =>
      queryOptions({
        queryKey: [
          ...samplingAssignmentQueries(global, config).all(),
          'samplers',
          organizationId ?? 'all',
        ],
        queryFn: () => api.samplers.list(organizationId),
        enabled: config.clause === 'new',
      }),

    samplerOrganizations: () =>
      queryOptions({
        queryKey: [...samplingAssignmentQueries(global, config).all(), 'sampler-organizations'],
        queryFn: () => api.samplerOrganizations.list(),
        enabled: config.clause === 'new' && config.capabilities.showSamplerOrganizationSelect,
      }),

    recap: (params?: {
      samplerIds?: number[];
      visible?: boolean;
      period?: string;
      view?: string;
    }) =>
      queryOptions({
        queryKey: [...samplingAssignmentQueries(global, config).all(), 'recap', params ?? {}],
        queryFn: () => api.recap.list(params),
        enabled: config.clause === 'new',
      }),

    smsProgress: (dispatchId: number) =>
      queryOptions({
        queryKey: [...samplingAssignmentQueries(global, config).all(), 'sms-progress', dispatchId],
        queryFn: () => api.smsProgress(dispatchId),
        enabled: dispatchId > 0,
      }),

    organisations: () =>
      queryOptions({
        queryKey: [...samplingAssignmentQueries(global, config).all(), 'organisations'],
        queryFn: () => organisationApi.getAll(ASSIGNMENT_MAP_ORGANISATIONS_FILTERS),
      }),

    api,
  };
};

export const gpSamplingAssignmentQueries = (global: Globals) =>
  samplingAssignmentQueries(global, GP_ASSIGNMENT_CONFIG);

export const getGpSamplingAssignmentQueries = () =>
  getQueriesWithGlobal(gpSamplingAssignmentQueries);

export const useGpSamplingAssignmentQueries = () =>
  useQueriesWithGlobal(gpSamplingAssignmentQueries);

export const getNgpSamplingAssignmentQueries = (campaignYear?: number) =>
  getQueriesWithGlobal((global: Globals) =>
    samplingAssignmentQueries(global, ngpAssignmentConfig(campaignYear)),
  );

export const useNgpSamplingAssignmentQueries = (campaignYear?: number) =>
  useQueriesWithGlobal((global: Globals) =>
    samplingAssignmentQueries(global, ngpAssignmentConfig(campaignYear)),
  );

export const getNgpTeamAssignmentQueries = (campaignYear?: number) =>
  getQueriesWithGlobal((global: Globals) =>
    samplingAssignmentQueries(global, ngpTeamAssignmentConfig(campaignYear)),
  );

export const useAssignmentQueries = (config: AssignmentMapConfig) =>
  useQueriesWithGlobal((global: Globals) => samplingAssignmentQueries(global, config));
